import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Check, CircleCheck, Flag, GraduationCap, Info, Keyboard, Layers, List, Pencil, Play, RotateCcw, X,
} from 'lucide-react';
import { authService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { buildTour, tourRole } from './tour/script';
import { createTourContext } from './tour/context';
import './Tour.css';

const POPOVER_W = 380;
const GAP = 14;
// Páginas em que o tour nunca abre sozinho
const QUIET = ['/login', '/trocar-senha', '/boas-vindas', '/preferencias', '/solicitar-acesso', '/conheca', '/apresentacao', '/a'];

const sel = (target) => `[data-tour="${target}"]`;
const reducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
// Recorte "fechado" no centro: a sombra cobre a tela toda (aberturas, troca de página)
const closedSpot = () => ({ top: window.innerHeight / 2, left: window.innerWidth / 2, width: 0, height: 0 });
const find = (target) => document.querySelector(sel(target));
const emptyProgress = { uso: { step: 0, done: false }, edicao: { step: 0, done: false } };

/** Passo onde retomar (0-based): no meio de um tour (mesmo refazendo um já concluído), senão do início. */
const resumeAt = (p, total) => (p.step > 0 && p.step < total ? p.step - 1 : 0);

function progressOf(user) {
  const t = user?.tour || {};
  return {
    uso: { step: t.uso?.step || 0, done: !!t.uso?.done },
    edicao: { step: t.edicao?.step || 0, done: !!t.edicao?.done },
  };
}

/** Rota do capítulo no convite de fim de capítulo: sem o prefixo do curso nem os parâmetros. */
function shortRoute(route) {
  const r = route.replace(/^\/curso\/:slug/, '').replace(/\/:[A-Za-z]+/g, '');
  return r || '/curso';
}

/** Retângulo do alvo (ou a união de todos, em passos com `all`), recortado à janela. */
function measureTarget(step) {
  const els = step.all ? [...document.querySelectorAll(sel(step.target))] : [find(step.target)].filter(Boolean);
  const rects = els.map(el => el.getBoundingClientRect()).filter(r => r.width > 0 || r.height > 0);
  if (!rects.length) return null;
  const top = Math.min(...rects.map(r => r.top));
  const left = Math.min(...rects.map(r => r.left));
  const bottom = Math.max(...rects.map(r => r.bottom));
  const right = Math.max(...rects.map(r => r.right));
  return { top, left, width: right - left, height: bottom - top, bottom, right };
}

function headerBottom() {
  const bar = document.querySelector('.ss-topbar');
  return bar ? Math.max(0, bar.getBoundingClientRect().bottom) : 0;
}

/** Leva o alvo para a área visível com espaço para o popover (window.scrollTo, sem scrollIntoView). */
function scrollToTarget(step, popH) {
  const el = find(step.target);
  if (!el || el.closest('.ss-topbar')) return;
  const r = measureTarget(step);
  if (!r) return;
  const vh = window.innerHeight;
  const head = headerBottom();
  const room = popH + GAP + 16;
  const visible = r.top >= head + 8 && r.bottom <= vh - 8;
  const fitsPopover = vh - r.bottom >= room || r.top - head >= room;
  if (visible && fitsPopover) return;
  // Alvo pequeno: centraliza alvo + popover; alvo alto: topo logo abaixo da barra
  const free = vh - head - r.height - room;
  const offset = free > 0 ? head + Math.min(free / 2, 80) : head + 16;
  window.scrollTo({ top: Math.max(0, window.scrollY + r.top - offset), behavior: reducedMotion() ? 'auto' : 'smooth' });
}

/**
 * Tour guiado em capítulos: navega pelas telas reais com spotlight.
 * Dois tours: uso (todos) e edição (admin e super admin). O progresso fica em user.tour.
 * Abre no fim do onboarding (state.tour), no 1º login de admin e pelo "Refazer tour" do menu.
 */
export function Tour() {
  const { user, setUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const role = tourRole(user);
  const canEdit = role !== 'user';

  const tours = useMemo(() => ({ uso: buildTour('uso', role), edicao: buildTour('edicao', role) }), [role]);
  const [progress, setProgress] = useState(emptyProgress);
  // mode: null | start (1a) | step (1b–1d) | index (1e) | done (1f)
  const [mode, setMode] = useState(null);
  const [kind, setKind] = useState('uso');
  const [index, setIndex] = useState(0);
  const [indexKind, setIndexKind] = useState('uso');
  const [then, setThen] = useState(null);
  const [target, setTarget] = useState(null); // { index, path } quando a rota do passo está resolvida
  const [rect, setRect] = useState(null);
  const [shown, setShown] = useState(false);
  const [centered, setCentered] = useState(false);
  const [waiting, setWaiting] = useState(null); // título do capítulo enquanto a página abre
  const [popH, setPopH] = useState(240);
  const [attempt, setAttempt] = useState(0); // força nova tentativa quando o índice não muda
  const [glide, setGlide] = useState(false); // recorte deslizando de um alvo para o outro
  const ctxRef = useRef(null);
  const dirRef = useRef(1);
  const popRef = useRef(null);
  const saveTimer = useRef(null);
  const cleanupRef = useRef(null);
  const autoOpened = useRef(false);
  const spotRef = useRef(null); // último recorte desenhado: fica parado enquanto o próximo alvo é procurado

  const steps = tours[kind].steps;
  const total = steps.length;
  const step = mode === 'step' ? steps[index] : null;

  // O progresso local acompanha o do servidor quando a conta muda
  useEffect(() => { setProgress(progressOf(user)); }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const persist = useCallback((data) => {
    authService.updateOnboarding(data).then(setUser).catch(() => { /* o tour é só orientação */ });
  }, [setUser]);

  const saveProgress = useCallback((k, patch, { now = false, extra } = {}) => {
    setProgress(p => ({ ...p, [k]: { ...p[k], ...patch } }));
    clearTimeout(saveTimer.current);
    const send = () => persist({ ...extra, tour: { [k]: patch } });
    if (now) send();
    else saveTimer.current = setTimeout(send, 700);
  }, [persist]);

  useEffect(() => () => clearTimeout(saveTimer.current), []);

  const runCleanup = useCallback(() => {
    const c = cleanupRef.current;
    cleanupRef.current = null;
    if (c) find(c)?.click();
  }, []);

  const resetStep = () => {
    setGlide(true);
    setTarget(null);
    setRect(null);
    setShown(false);
    setCentered(false);
    setWaiting(null);
  };

  /** Começa (ou retoma) um tour no passo i. */
  const begin = useCallback((k, i = 0, next = null) => {
    if (!ctxRef.current) ctxRef.current = createTourContext();
    runCleanup();
    resetStep();
    dirRef.current = 1;
    setAttempt(a => a + 1);
    setKind(k);
    setThen(next);
    setIndex(Math.max(0, Math.min(i, tours[k].steps.length - 1)));
    setMode('step');
    if (!user?.tourSeen) persist({ tourSeen: true });
  }, [tours, user?.tourSeen, persist, runCleanup]);

  const closeAll = useCallback(() => {
    runCleanup();
    resetStep();
    setMode(null);
  }, [runCleanup]);

  /** Fecha guardando a posição (Esc, X, "Pausar aqui"): não marca como concluído. */
  const pause = useCallback(() => {
    if (mode === 'step') saveProgress(kind, { step: index + 1 }, { now: true });
    closeAll();
  }, [mode, kind, index, saveProgress, closeAll]);

  const finish = useCallback(() => {
    runCleanup();
    resetStep();
    saveProgress(kind, { step: total, done: true }, { now: true, extra: { tourSeen: true, done: true } });
    if (kind === 'uso' && then === 'edicao' && tours.edicao.steps.length) {
      begin('edicao', 0);
      return;
    }
    setMode('done');
  }, [kind, total, then, tours, saveProgress, begin, runCleanup]);

  const go = useCallback((i, dir = i >= index ? 1 : -1) => {
    if (i >= total) { finish(); return; }
    runCleanup();
    resetStep();
    // Antes do primeiro passo não há para onde voltar: segue adiante
    dirRef.current = i < 0 ? 1 : dir;
    const next = Math.max(0, i);
    setAttempt(a => a + 1);
    setIndex(next);
    saveProgress(kind, { step: next + 1 });
  }, [index, total, kind, finish, saveProgress, runCleanup]);

  // Abertura: menu (índice), fim do onboarding e 1º login de admin
  useEffect(() => {
    const openIndex = () => {
      runCleanup();
      resetStep();
      setIndexKind(kind);
      setMode('index');
    };
    window.addEventListener('tour:index', openIndex);
    window.addEventListener('tour:start', openIndex);
    return () => {
      window.removeEventListener('tour:index', openIndex);
      window.removeEventListener('tour:start', openIndex);
    };
  }, [kind, runCleanup]);

  useEffect(() => {
    if (location.state?.tour && user) {
      setMode('start');
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.state, location.pathname, navigate, user]);

  useEffect(() => {
    if (!user || autoOpened.current || mode) return;
    if (!canEdit || user.tourSeen || user.mustChangePassword || user.demo) return;
    if (QUIET.some(p => location.pathname === p || location.pathname.startsWith(`${p}/`))) return;
    autoOpened.current = true;
    setMode('start');
  }, [user, canEdit, mode, location.pathname]);

  // 1) Resolve a rota do passo; sem curso/trilha/artigo, o passo é pulado
  useEffect(() => {
    if (mode !== 'step' || !step) return undefined;
    let alive = true;
    ctxRef.current.resolve(step.route).then((path) => {
      if (!alive) return;
      if (!path) { go(index + dirRef.current, dirRef.current); return; }
      setTarget({ index, path });
    });
    return () => { alive = false; };
  }, [mode, index, kind, attempt]); // eslint-disable-line react-hooks/exhaustive-deps

  // 2) Navega para a página do passo
  useEffect(() => {
    if (!target || target.index !== index) return;
    if (decodeURI(location.pathname) !== decodeURI(target.path)) {
      setWaiting(step.chapter.title);
      navigate(target.path);
    }
  }, [target]); // eslint-disable-line react-hooks/exhaustive-deps

  // 3) Espera o alvo aparecer (abre abas/etapas do `prep`); some de vez → pula o passo
  useEffect(() => {
    if (!target || target.index !== index || !step) return undefined;
    const started = Date.now();
    const limit = decodeURI(window.location.pathname) !== decodeURI(target.path) ? 4500 : 2500;
    let missingSince = null;
    let prepIndex = 0;
    const tick = setInterval(() => {
      const onPage = decodeURI(window.location.pathname) === decodeURI(target.path);
      // Abertura do capítulo: centralizada, assim que a página termina de carregar
      if (step.intro) {
        if (!onPage || document.querySelector('.ss-loader')) {
          if (Date.now() - started < limit) return;
        }
        clearInterval(tick);
        setWaiting(null);
        setCentered(true);
        setShown(true);
        return;
      }
      const found = onPage && measureTarget(step);
      if (found) {
        clearInterval(tick);
        if (step.cleanup) cleanupRef.current = step.cleanup;
        scrollToTarget(step, popH);
        setWaiting(null);
        setCentered(false);
        setShown(true);
        return;
      }
      const loading = !onPage || document.querySelector('.ss-loader');
      if (!loading && step.prep && prepIndex < step.prep.length) {
        const el = find(step.prep[prepIndex]);
        prepIndex += 1;
        if (el) {
          if (step.cleanup) cleanupRef.current = step.cleanup;
          el.click();
          missingSince = null;
          return;
        }
      }
      if (!loading) missingSince = missingSince ?? Date.now();
      const settledMissing = missingSince && Date.now() - missingSince > 900;
      if (settledMissing || Date.now() - started > limit) {
        clearInterval(tick);
        setWaiting(null);
        const lastOfChapter = step.indexInChapter === step.chapter.count - 1;
        // O fim de capítulo mostra o convite mesmo sem alvo; os demais passos somem
        if (onPage && lastOfChapter && dirRef.current > 0) { setCentered(true); setShown(true); return; }
        go(index + dirRef.current, dirRef.current);
      }
    }, 120);
    return () => clearInterval(tick);
  }, [target]); // eslint-disable-line react-hooks/exhaustive-deps

  // Posição: recalcula em resize/scroll e enquanto a página termina de carregar
  const measure = useCallback(() => {
    if (!step || !shown || centered) return;
    setRect(measureTarget(step));
  }, [step, shown, centered]);

  useLayoutEffect(() => {
    if (!shown) return undefined;
    measure();
    const t = setInterval(measure, 400);
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      clearInterval(t);
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [shown, measure]);

  useLayoutEffect(() => {
    if (popRef.current) setPopH(popRef.current.offsetHeight);
  }, [shown, index, rect?.width]);

  // A transição do recorte vale só na troca de passo; depois ele acompanha a rolagem sem atraso
  useEffect(() => {
    if (!shown) return undefined;
    const t = setTimeout(() => setGlide(false), 700);
    return () => clearTimeout(t);
  }, [shown, index]);

  // Teclado: → próximo, ← voltar, Esc pausa (antes dos atalhos da página)
  useEffect(() => {
    if (!mode) return undefined;
    const onKey = (e) => {
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName) && e.key !== 'Escape') return;
      if (e.key === 'Escape') {
        if (mode === 'step') pause(); else if (mode === 'start') later(); else closeAll();
      } else if (mode === 'step' && shown && e.key === 'ArrowRight') go(index + 1, 1);
      else if (mode === 'step' && shown && e.key === 'ArrowLeft' && index > 0) go(index - 1, -1);
      else return;
      e.preventDefault();
      e.stopImmediatePropagation();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  });

  const later = () => {
    persist({ tourSeen: true });
    closeAll();
  };

  if (!user || !mode) return null;

  if (mode === 'start') {
    return <StartModal user={user} role={role} tours={tours} progress={progress} onLater={later} onStart={begin} />;
  }
  if (mode === 'index') {
    return (
      <IndexModal
        tours={tours}
        progress={progress}
        canEdit={canEdit}
        kind={indexKind}
        onKind={setIndexKind}
        onClose={closeAll}
        onStart={begin}
      />
    );
  }
  if (mode === 'done') {
    return (
      <DoneModal
        kind={kind}
        tours={tours}
        canEdit={canEdit}
        onEdit={() => begin('edicao', 0)}
        onResume={async () => {
          closeAll();
          const ctx = ctxRef.current || createTourContext();
          navigate(await ctx.resumePath());
        }}
        onClose={closeAll}
      />
    );
  }

  if (!step) return null;
  if (!shown) {
    // Entre um passo e outro o recorte fica onde estava; trocando de página, fecha no centro
    const hold = waiting || !spotRef.current ? closedSpot() : spotRef.current;
    return (
      <div className="tour tour--busy" aria-hidden={!waiting}>
        <div className={`tour__spot is-glide ${hold.width ? '' : 'is-closed'}`} style={hold} aria-hidden="true" />
        {waiting && (
          <div className="tour__toast" role="status">
            <span className="tour__spinner" aria-hidden="true" />Abrindo {waiting}…
          </div>
        )}
      </div>
    );
  }

  const { chapter } = step;
  const chapters = tours[kind].chapters;
  const lastOfChapter = step.indexInChapter === chapter.count - 1;
  const nextChapter = lastOfChapter ? chapters[chapter.number] : null;
  const isLast = index === total - 1;

  // Popover abaixo do alvo, acima se não couber; alvo alto: preso no rodapé da janela
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const width = vw < 520 ? vw - 24 : nextChapter || step.intro ? 420 : POPOVER_W;
  const pad = 6;
  let style;
  let arrow = null;
  let place = 'center';
  if (rect && !centered) {
    const left = Math.max(12, Math.min(vw - width - 12, rect.left + rect.width / 2 - width / 2));
    if (rect.bottom + GAP + popH <= vh - 8) {
      place = 'below';
      style = { top: rect.bottom + GAP, left };
    } else if (rect.top - GAP - popH >= 8) {
      place = 'above';
      style = { top: rect.top - GAP - popH, left };
    } else {
      place = 'dock';
      style = { top: Math.max(8, vh - popH - 16), left: vw < 520 ? 12 : Math.max(12, vw - width - 24) };
    }
    if (place !== 'dock') arrow = Math.max(18, Math.min(width - 18, rect.left + rect.width / 2 - left));
  } else {
    style = { top: Math.max(12, (vh - popH) / 2), left: Math.max(12, (vw - width) / 2) };
  }
  let spot = null;
  if (rect && !centered) {
    const top = Math.max(2, rect.top - pad);
    const left = Math.max(2, rect.left - pad);
    spot = { top, left, width: Math.min(vw - 2, rect.right + pad) - left, height: Math.min(vh - 2, rect.bottom + pad) - top };
  }
  spotRef.current = spot;
  const intro = !!step.intro;

  const edition = kind === 'edicao';
  const pct = Math.round(((index + 1) / total) * 100);

  return (
    <div className="tour" role="dialog" aria-modal="true" aria-labelledby="tour-title">
      <div className={`tour__spot ${glide ? 'is-glide' : ''} ${spot ? '' : 'is-closed'}`} style={spot || closedSpot()} aria-hidden="true" />
      <div key={`${kind}-${index}`} ref={popRef} className={`tour__pop tour__pop--${place} ${intro ? 'tour__pop--intro' : ''}`} style={{ ...style, width }}>
        {arrow != null && <span className="tour__arrow" style={{ left: arrow }} aria-hidden="true" />}
        <div className="tour__body">
          <div className="tour__head">
            {nextChapter ? (
              <span className="tour__eyebrow"><CircleCheck size={13} aria-hidden="true" />Cap. {chapter.number} concluído</span>
            ) : (
              <>
                {edition && <span className="tour__tag"><Pencil size={10} aria-hidden="true" />Edição</span>}
                <span className="tour__eyebrow">Cap. {chapter.number} · {chapter.title}</span>
                <span className="tour__count">{step.indexInChapter + 1} de {chapter.count}</span>
              </>
            )}
            <button type="button" className="tour__close" onClick={pause} aria-label="Pausar tour"><X size={15} /></button>
          </div>
          {intro && <span className="tour__intro-icon" aria-hidden="true"><chapter.icon size={22} /></span>}
          <h2 id="tour-title" className="tour__title">{step.title}</h2>
          <p className="tour__text">{step.text}</p>
          {step.form && (
            <p className="tour__note"><Info size={13} aria-hidden="true" />O tour não cria nada: o formulário só abre de exemplo.</p>
          )}
          {centered && !intro && !step.form && (
            <p className="tour__note tour__note--muted"><Info size={13} aria-hidden="true" />Este item não aparece na sua tela agora.</p>
          )}

          {nextChapter ? (
            <>
              <div className="tour__next-chapter">
                <span className="tour__next-icon"><nextChapter.icon size={16} aria-hidden="true" /></span>
                <span className="tour__next-text">
                  <span className="tour__next-eyebrow">Próximo · Cap. {nextChapter.number} de {chapters.length}</span>
                  <strong>{nextChapter.title} · {nextChapter.count} {nextChapter.count === 1 ? 'passo' : 'passos'}</strong>
                </span>
                <code className="tour__next-route">{shortRoute(nextChapter.route)}</code>
              </div>
              <div className="tour__foot tour__foot--chapter">
                <button type="button" className="tour__pause" onClick={pause}>Pausar aqui</button>
                <button type="button" className="tour__back" onClick={() => go(index - 1, -1)}>
                  <ArrowLeft size={14} aria-hidden="true" />Voltar
                </button>
                <button type="button" className="tour__next tour__next--lime" onClick={() => go(index + 1, 1)}>
                  Ir para {nextChapter.title}<ArrowRight size={15} aria-hidden="true" />
                </button>
              </div>
            </>
          ) : (
            <div className="tour__foot">
              <span className="tour__dots" aria-hidden="true">
                {Array.from({ length: chapter.count }, (_, i) => (
                  <i key={i} className={i === step.indexInChapter ? 'is-on' : i < step.indexInChapter ? 'is-seen' : ''} />
                ))}
              </span>
              {index > 0 && (
                <button type="button" className="tour__back" onClick={() => go(index - 1, -1)}>
                  <ArrowLeft size={14} aria-hidden="true" />Voltar
                </button>
              )}
              <button type="button" className="tour__next" onClick={() => (isLast ? finish() : go(index + 1, 1))}>
                {isLast ? 'Concluir' : 'Próximo'}
                {isLast ? <Check size={15} aria-hidden="true" /> : <ArrowRight size={15} aria-hidden="true" />}
              </button>
            </div>
          )}
        </div>
        {!nextChapter && (
          <div className="tour__bar">
            <span className="tour__bar-count">Passo {index + 1} de {total}</span>
            <span className="tour__bar-track" aria-hidden="true"><span style={{ width: `${pct}%` }} /></span>
            <button type="button" className="tour__bar-link" onClick={() => { setIndexKind(kind); runCleanup(); resetStep(); saveProgress(kind, { step: index + 1 }, { now: true }); setMode('index'); }}>
              <List size={13} aria-hidden="true" />Capítulos
            </button>
            {chapter.number < chapters.length && (
              <button type="button" className="tour__bar-link tour__bar-link--muted" onClick={() => go(chapter.start + chapter.count, 1)}>
                Pular capítulo
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/** 1a: boas-vindas. Usuário só começa; admin e super admin escolhem o tour. */
function StartModal({ user, role, tours, progress, onLater, onStart }) {
  const [choice, setChoice] = useState('uso');
  const canEdit = role !== 'user';
  const uso = tours.uso.steps.length;
  const ed = tours.edicao.steps.length;
  const first = (user.displayName || user.name || '').split(' ')[0];
  const roleLabel = role === 'super' ? 'super admin' : 'admin';
  const choices = [
    { value: 'uso', icon: GraduationCap, title: 'Usar a plataforma', text: 'Cursos, estudo, simulados, trilhas e artigos', meta: `${uso} passos · ~10 min` },
    { value: 'edicao', icon: Pencil, title: 'Editar conteúdo', text: role === 'super' ? 'Tópicos, aulas, trilhas, artigos e usuários' : 'Tópicos, aulas, trilhas e artigos', meta: `${ed} passos · ~5 min` },
    { value: 'both', icon: Layers, title: 'Os dois, em sequência', text: 'Uso primeiro; a edição começa ao terminar', meta: `${uso + ed} passos` },
  ];
  const start = () => {
    if (choice === 'edicao') onStart('edicao', resumeAt(progress.edicao, ed));
    else onStart('uso', resumeAt(progress.uso, uso), choice === 'both' ? 'edicao' : null);
  };

  return (
    <div className="tour-modal" role="dialog" aria-modal="true" aria-labelledby="tour-start-title">
      <div className="tour-modal__card tour-modal__card--start">
        <div className="tour-start__head">
          <span className="tour-start__eyebrow">Bem-vindo, {first}</span>
          <h2 id="tour-start-title" className="tour-start__title">Vamos conhecer o StudySpace?</h2>
          <p className="tour-start__text">
            {canEdit ? (
              <>Você tem acesso de <strong>{roleLabel}</strong>. Escolha por onde começar; o outro tour fica no menu.</>
            ) : (
              <>Um passeio pelas telas reais: cursos, estudo, simulados, trilhas e artigos. Dá para pausar e retomar pelo menu.</>
            )}
          </p>
        </div>
        <div className="tour-start__body">
          {canEdit && (
            <div className="tour-start__choices" role="radiogroup" aria-label="Qual tour">
              {choices.map(c => (
                <button
                  key={c.value}
                  type="button"
                  role="radio"
                  aria-checked={choice === c.value}
                  className={`tour-choice ${choice === c.value ? 'is-on' : ''}`}
                  onClick={() => setChoice(c.value)}
                >
                  <span className="tour-choice__icon"><c.icon size={18} aria-hidden="true" /></span>
                  <span className="tour-choice__text"><strong>{c.title}</strong><span>{c.text}</span></span>
                  <span className="tour-choice__meta">{c.meta}</span>
                  <span className="tour-choice__radio" aria-hidden="true" />
                </button>
              ))}
            </div>
          )}
          {!canEdit && (
            <div className="tour-start__info">
              <span className="tour-start__meta">{uso} passos · ~10 min</span>
              <span className="tour-start__keys"><Keyboard size={14} aria-hidden="true" />Use ← → para navegar e Esc para sair</span>
            </div>
          )}
          <div className="tour-start__foot">
            {canEdit && <span className="tour-start__keys"><Keyboard size={14} aria-hidden="true" />Use ← → para navegar e Esc para sair</span>}
            <button type="button" className="tour-btn tour-btn--ghost" onClick={onLater}>Agora não</button>
            <button type="button" className="tour-btn tour-btn--navy" onClick={start} autoFocus>
              Começar tour<ArrowRight size={15} aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** 1e: índice de capítulos, aberto pelo "Refazer tour". */
function IndexModal({ tours, progress, canEdit, kind, onKind, onClose, onStart }) {
  const { chapters, steps } = tours[kind];
  const p = progress[kind];
  const total = steps.length;
  // Refazendo um tour concluído, vale a posição atual
  const inProgress = p.step > 0 && p.step < total;
  const allDone = p.done && !inProgress;
  const seen = allDone ? total : Math.min(p.step, total);
  const current = inProgress ? p.step - 1 : -1;
  const count = (k) => {
    const n = tours[k].steps.length;
    const q = progress[k];
    return `${q.done && !(q.step > 0 && q.step < n) ? n : Math.min(q.step, n)}/${n}`;
  };

  return (
    <div className="tour-modal" role="dialog" aria-modal="true" aria-labelledby="tour-index-title" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="tour-modal__card tour-modal__card--index">
        <div className="tour-index__head">
          <div className="tour-index__row">
            <h2 id="tour-index-title" className="tour-index__title">Tour do StudySpace</h2>
            <button type="button" className="tour__close" onClick={onClose} aria-label="Fechar"><X size={16} /></button>
          </div>
          <div className="tour-index__row">
            {canEdit && (
              <div className="tour-index__seg" role="tablist" aria-label="Tour">
                {['uso', 'edicao'].map(k => (
                  <button key={k} type="button" role="tab" aria-selected={kind === k} className={kind === k ? 'is-on' : ''} onClick={() => onKind(k)}>
                    {k === 'uso' ? 'Uso' : 'Edição'} · {count(k)}
                  </button>
                ))}
              </div>
            )}
            <span className="tour-index__track" aria-label={`${seen} de ${total} passos vistos`}>
              <span style={{ width: `${total ? (seen / total) * 100 : 0}%` }} />
            </span>
          </div>
        </div>
        <ol className="tour-index__list">
          {chapters.map(c => {
            const end = c.start + c.count - 1;
            const done = allDone || current > end;
            const now = !done && current >= c.start && current <= end;
            const status = done ? 'Visto' : now ? `Passo ${current + 1} · ${current - c.start + 1} de ${c.count}` : `${c.count} ${c.count === 1 ? 'passo' : 'passos'}`;
            const Icon = done ? Check : c.icon;
            return (
              <li key={c.id}>
                <button type="button" className={`tour-chapter ${done ? 'is-done' : ''} ${now ? 'is-now' : ''}`} onClick={() => onStart(kind, now ? current : c.start)}>
                  <span className="tour-chapter__icon"><Icon size={14} aria-hidden="true" /></span>
                  <span className="tour-chapter__num">{String(c.number).padStart(2, '0')}</span>
                  <span className="tour-chapter__name">{c.title}</span>
                  <span className="tour-chapter__status">{status}</span>
                  <ArrowRight size={14} className="tour-chapter__go" aria-hidden="true" />
                </button>
              </li>
            );
          })}
        </ol>
        <div className="tour-index__foot">
          {(inProgress || allDone) && (
            <button type="button" className="tour-btn tour-btn--ghost" onClick={() => onStart(kind, 0)}>
              <RotateCcw size={14} aria-hidden="true" />Recomeçar do zero
            </button>
          )}
          <span className="tour-index__spacer" />
          <button type="button" className="tour-btn tour-btn--navy" onClick={() => onStart(kind, resumeAt(p, total))}>
            {inProgress ? `Continuar do passo ${p.step}` : allDone ? 'Fazer de novo' : 'Começar tour'}
            <ArrowRight size={15} aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );
}

/** 1f: conclusão. */
function DoneModal({ kind, tours, canEdit, onEdit, onResume, onClose }) {
  const uso = kind === 'uso';
  const n = tours[kind].steps.length;
  return (
    <div className="tour-modal" role="dialog" aria-modal="true" aria-labelledby="tour-done-title" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="tour-modal__card tour-modal__card--done">
        <span className="tour-done__icon"><Flag size={28} aria-hidden="true" /></span>
        <span className="tour-done__eyebrow">{uso ? 'Tour de uso concluído' : 'Tour de edição concluído'}</span>
        <h2 id="tour-done-title" className="tour-done__title">{uso ? 'Pronto para estudar' : 'Pronto para editar'}</h2>
        <p className="tour-done__text">
          Você viu os {n} passos. Para rever qualquer capítulo, é só abrir <strong>Refazer tour</strong> no menu.
        </p>
        <div className="tour-done__actions">
          {uso && canEdit && tours.edicao.steps.length > 0 && (
            <button type="button" className="tour-btn tour-btn--navy tour-btn--block" onClick={onEdit}>
              <Pencil size={14} className="tour-btn__lime" aria-hidden="true" />Fazer o tour de edição · {tours.edicao.steps.length} passos
            </button>
          )}
          <button type="button" className="tour-btn tour-btn--outline tour-btn--block" onClick={onResume}>
            <Play size={14} aria-hidden="true" />Continuar de onde parei no curso
          </button>
        </div>
      </div>
    </div>
  );
}
